import mustache from "mustache";
import { AnalisisLaboratorioResponse } from "../../../../modulos/fertilizacion/response/AnalisisLaboratorioResponse";
import { ClienteFincaLoteResponse } from "../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { formatMesDia } from "../../../../utils/fechaUtils";
import {
  tipoAnalisisLabel,
  urgenciaColor,
} from "../../../../utils/fertilizacionUtils";
import { _2d } from "../../../../utils/numberUtils";
import { BaseSecondSidebarItemRender } from "../../../base/sidebar/BaseSecondSidebarItemRender";
import template from "./fertilizacionLotesContentTemplate.html";

mustache.parse(template);

/** Contador para ids únicos por instancia (contenedor de la lista de lotes). */
let FERTI_ITEM_ID = 0;

/** Prefijo de las alertas que NO se muestran: la de cultivo sin confirmar es de
 *  estado (clasificación pendiente de asignación manual), no agronómica. */
const ALERTA_OCULTA_PREFIX = "Cultivo no confirmado";

/** Item del sidebar de fertilización: label con fecha + tipo y, en el contenido
 *  expandido, un header con el laboratorio + acciones (editar = registrar lotes,
 *  eliminar) y la lista de lotes del análisis (nombre + cultivo · área + dot del
 *  peor parámetro + alertas). Si el análisis tiene lotes pendientes, muestra un
 *  hint en vez de la lista. Los lotes se resuelven vía un callback compartido. */
export class FertilizacionSidebarItemRender extends BaseSecondSidebarItemRender {
  /** Id único de la instancia (para el contenedor de la lista y los botones). */
  private readonly _fertiItemId: number;
  /** Si el contenido expandido se colapsó/dispuso (corta la carga async). */
  private _disposedExpanded = false;
  /** Botón editar (disponible mientras el item está expandido). */
  private _editBtn: HTMLElement | null = null;
  /** Botón eliminar (disponible mientras el item está expandido). */
  private _deleteBtn: HTMLElement | null = null;
  /** Botón "Analizar Finca" (disponible mientras el item está expandido). */
  private _analizarBtn: HTMLElement | null = null;

  /**
   * @param _analisis   Análisis del item.
   * @param _onSelected Notifica el análisis al seleccionarse el item.
   * @param _getLotes   Provee los lotes de la finca (promise compartida por el
   *                    sidebar; una sola carga real).
   * @param _onEdit     Editar el análisis (abre el registro de lotes).
   * @param _onDelete   Eliminar el análisis (con confirmación).
   * @param _onAnalizar Abrir el diagnóstico del análisis.
   */
  constructor(
    private readonly _analisis: AnalisisLaboratorioResponse,
    private readonly _onSelected: (analisis: AnalisisLaboratorioResponse) => void,
    private readonly _getLotes: () => Promise<ClienteFincaLoteResponse[]>,
    private readonly _onEdit: (analisis: AnalisisLaboratorioResponse) => void,
    private readonly _onDelete: (analisis: AnalisisLaboratorioResponse) => void,
    private readonly _onAnalizar: (
      analisis: AnalisisLaboratorioResponse,
    ) => void,
  ) {
    super();
    FERTI_ITEM_ID += 1;
    this._fertiItemId = FERTI_ITEM_ID;
  }

  /** Fecha del análisis + tipo (p. ej. "Marzo 04 · Edáfico"). */
  protected getLabel(): string {
    return `${formatMesDia(this._analisis.fechaAnalisis)} · ${this._tipoLabel()}`;
  }

  /** El laboratorio va en el header del contenido (con las acciones), no en el
   *  título base. */
  protected getTitle(): string {
    return "";
  }

  /** Header (laboratorio + editar/eliminar) + lista de lotes (o hint si faltan
   *  lotes por asignar). */
  protected getItemContent(): string {
    return this._buildHeader() + this._buildBody();
  }

  /** Al expandirse: cablea editar/eliminar y, si no hay pendientes, resuelve los
   *  lotes y pinta la lista. */
  protected connectExpanded(): void {
    this._disposedExpanded = false;
    this._editBtn = document.getElementById(
      `ferti-item-edit-${this._fertiItemId}`,
    );
    this._deleteBtn = document.getElementById(
      `ferti-item-delete-${this._fertiItemId}`,
    );
    this._analizarBtn = document.getElementById(
      `ferti-item-analizar-${this._fertiItemId}`,
    );
    this._editBtn?.addEventListener("click", this._onEditClick);
    this._deleteBtn?.addEventListener("click", this._onDeleteClick);
    this._analizarBtn?.addEventListener("click", this._onAnalizarClick);
    if (this._analisis.pendingLotsCount > 0) return;
    void this._loadLotes();
  }

  /** Al colapsarse/disponer: corta la carga async y libera los botones. */
  protected disposeExpanded(): void {
    this._disposedExpanded = true;
    this._editBtn?.removeEventListener("click", this._onEditClick);
    this._deleteBtn?.removeEventListener("click", this._onDeleteClick);
    this._analizarBtn?.removeEventListener("click", this._onAnalizarClick);
    this._editBtn = null;
    this._deleteBtn = null;
    this._analizarBtn = null;
  }

  /** Al seleccionarse: notifica el análisis. */
  protected onSelected(): void {
    this._onSelected(this._analisis);
  }

  private _onEditClick = (): void => this._onEdit(this._analisis);
  private _onDeleteClick = (): void => this._onDelete(this._analisis);
  private _onAnalizarClick = (): void => this._onAnalizar(this._analisis);

  /** Header del detalle: laboratorio + acciones editar/eliminar. */
  private _buildHeader(): string {
    return `<div class="ss-subheader">
      <span>${this._laboratorioCorto()}</span>
      <span class="ss-subheader-actions">
        <button type="button" class="sx-list-btn" title="Registrar lotes" id="ferti-item-edit-${this._fertiItemId}"><i class="ph ph-pencil-simple"></i></button>
        <button type="button" class="sx-list-btn sx-list-btn--danger" title="Eliminar análisis" id="ferti-item-delete-${this._fertiItemId}"><i class="ph ph-trash"></i></button>
      </span>
    </div>`;
  }

  /** Cuerpo del detalle: lista de lotes (contenedor que se llena async) +
   *  "Analizar Finca", o hint si faltan lotes por asignar (sin lotes resueltos
   *  no hay nada que diagnosticar). */
  private _buildBody(): string {
    const pendientes = this._analisis.pendingLotsCount;
    if (pendientes > 0) {
      return `<div class="ss-hint">Faltan ${pendientes} lote(s) por asignar.</div>`;
    }
    return `<div class="ferti-lotes" id="ferti-lotes-${this._fertiItemId}"><div class="ss-hint">Cargando…</div></div>
      <button type="button" class="sx-btn sx-btn-outline sx-btn-block ferti-analizar-btn" id="ferti-item-analizar-${this._fertiItemId}"><i class="ph ph-chart-bar"></i>Analizar Finca</button>`;
  }

  /** Resuelve los lotes y pinta la lista dentro del contenedor de la instancia
   *  (no-op si el item ya se colapsó o el contenedor no existe). */
  private async _loadLotes(): Promise<void> {
    const lotes = await this._getLotes();
    if (this._disposedExpanded) return;
    const cont = document.getElementById(`ferti-lotes-${this._fertiItemId}`);
    if (!cont) return;
    cont.innerHTML = this._buildRows(lotes);
  }

  /** Arma las filas: por cada registro con lote resuelto, nombre + cultivo ·
   *  área + dot del peor parámetro, y sus alertas agronómicas si las hay. */
  private _buildRows(lotes: ClienteFincaLoteResponse[]): string {
    const loteById = new Map(lotes.map((lote) => [lote.id, lote]));
    const rows = this._analisis.registros
      .map((registro) => {
        const lote = registro.loteId
          ? loteById.get(registro.loteId)
          : undefined;
        if (!lote) return null;
        return {
          nombre: lote.nombreLote,
          cultivo: lote.cultivo?.nombre ?? "Sin especificar",
          area: _2d(lote.areaHectareas),
          color: urgenciaColor(registro),
          alertas: registro.alertas.filter(
            (alerta) => !alerta.startsWith(ALERTA_OCULTA_PREFIX),
          ),
        };
      })
      .filter((row) => row !== null);
    return mustache.render(template, { lotes: rows });
  }

  /** Laboratorio sin el sufijo "— Análisis de … (prefijo …)": se queda con la
   *  parte antes del "—" (ej. "Agrilab Colombia"). Si no viene el "—", devuelve
   *  el nombre completo. */
  private _laboratorioCorto(): string {
    return (
      (this._analisis.nombreLaboratorio ?? "").split("—")[0].trim() || "Análisis"
    );
  }

  /** Tipo de análisis en español. */
  private _tipoLabel(): string {
    return tipoAnalisisLabel(this._analisis.tipoAnalisis);
  }
}
